import { React, useState, useRef, useEffect, useCallback } from 'react'
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Spin, message } from 'antd';
import RenderCustomComponent from './RenderCustomComponent';
import RenderStandardComponent from './RenderStandardComponent';
import ApiService from '../Components/apiService';
import { BASE_URL } from '../Components/Constant';
import eventBus from "../Components/eventBus";

const ResponsiveGridLayout = WidthProvider(Responsive);
const breakpoints = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 320 };
const cols = { lg: 18, md: 18, sm: 9, xs: 6, xxs: 3 };
const rowHeight = 5;

const RecordGridLayoutView = ({ gridLayoutData, objectName, recordId,alerts }) => {
  const [components, setComponents] = useState([]);
  const [layout, setLayout] = useState([]);
  const [compactComponent, setCompactComponent] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);

  // Function to generate row styles based on highlight configuration
  // This function evaluates highlight filters and returns inline CSS styles for table rows
  const getRowStyleForView = (record, component) => {


    if (!component?.multipleHighlights || !record || !Array.isArray(component.multipleHighlights)) {
      return { style: {}, message: null };
    }

    // Check each highlight rule and return the first matching one
    for (const highlight of component.multipleHighlights) {
      if (!highlight.filters || Object.keys(highlight.filters).length === 0) {
        continue;
      }

      const filters = Object.values(highlight.filters);
      const logic = highlight.filterLogic || 'AND';

      // Check if record matches highlight conditions
      const matchResults = filters.map(filter => {
        if (!filter.field || filter.field === '') return false;

        const recordValue = record[filter.field];
        const filterValue = typeof filter.value === 'object'
          ? Object.values(filter.value)[0]
          : filter.value;

        if (typeof filter.value === 'object' && filter.value !== null) {
          const operator = Object.keys(filter.value)[0];
          switch (operator) {
            case 'equal':
              return recordValue === filterValue;
            case 'not_equal':
              return recordValue !== filterValue;
            case 'contains':
              return recordValue && recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
            case 'not_contains':
              return !recordValue || !recordValue.toString().toLowerCase().includes(filterValue.toString().toLowerCase());
            case 'starts_with':
              return recordValue && recordValue.toString().toLowerCase().startsWith(filterValue.toString().toLowerCase());
            case 'ends_with':
              return recordValue && recordValue.toString().toLowerCase().endsWith(filterValue.toString().toLowerCase());
            case 'greater_than':
              return Number(recordValue) > Number(filterValue);
            case 'less_than':
              return Number(recordValue) < Number(filterValue);
            case 'greater_equal':
              return Number(recordValue) >= Number(filterValue);
            case 'less_equal':
              return Number(recordValue) <= Number(filterValue);
            default:
              return recordValue === filterValue;
          }
        } else {
          return recordValue === filterValue;
        }
      });

      // Apply logic (AND/OR)
      let matches = false;
      if (logic === 'AND') {
        matches = matchResults.length > 0 && matchResults.every(result => result === true);
      } else if (logic === 'OR') {
        matches = matchResults.some(result => result === true);
      } else if (logic === '1' && matchResults.length === 1) {
        matches = matchResults[0];
      }

      // Return highlight style if matches (first match wins)
      if (matches) {
        return {
          style: {
            backgroundColor: highlight.backgroundColor,
            border: `1px solid ${highlight.borderColor}`,
            color: highlight.textColor,
            fontWeight: highlight.bold ? 'bold' : 'normal'
          },
          message: highlight.message || null
        };
      }
    }
    return { style: {}, message: null };
  };
  const functionRef = useRef(null);
  const [relatedLists, setRelatedLists] = useState([]);
  const [transientLoading, setTransientLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [record, setRecord] = useState({});
  const [layoutLoading, setLayoutLoading] = useState(false);
  const initializingRef = useRef(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [recordPathField, setRecordPathField] = useState(null);
  const [currentSelectedValue, setCurrentSelectedValue] = useState([]);
  const [isValueUpdated, setIsValueUpdated] = useState(false);
  const [picklistValues, setPicklistValues] = useState([]); // For picklist values
  const [picklistFieldNameForRecordPath, setPicklistFieldNameForRecordPath] = useState(''); // For picklist field name
  useEffect(() => {

    if (record && recordPathField && picklistValues.length > 0) {
      const fieldMeta = metadata?.fields.find(f => f._id === recordPathField);
      const fieldApiName = fieldMeta?.name;
      // const fieldMeta = fields.find(f => f._id === recordPathField);
      // const fieldApiName = fieldMeta?.name;

      setCurrentSelectedValue(record[fieldApiName]);


      // setCurrentFieldApiName(fieldApiName);
      const currentValue = record[fieldApiName];

      const index = picklistValues.findIndex((val) => val === currentValue);
      if (index !== -1) {
        setSelectedIndex(index);
      } else {
        setSelectedIndex(null);
      }
    }
  }, [record, recordPathField, picklistValues, metadata?.fields]);

  useEffect(() => {
    const handleLoadingStart = (message = 'Loading...') => {
      setTransientLoading(true);
      setLoadingMessage(message);
    };

    const handleLoadingEnd = () => {
      setTransientLoading(false);
      setLoadingMessage('');
    };

    eventBus.on("loadingStart", handleLoadingStart);
    eventBus.on("loadingEnd", handleLoadingEnd);

    return () => {
      eventBus.off("loadingStart", handleLoadingStart);
      eventBus.off("loadingEnd", handleLoadingEnd);
    };
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      const apiService = new ApiService(`${BASE_URL}/fetch_single_record/${objectName}/${recordId}`, {}, 'GET');
      const responseData = await apiService.makeCall();
      setRecord(responseData);
      return responseData;
    }
    catch (error) {
      console.error('Error fetching record:', error);
      return null;
    }
  }, [objectName, recordId]);


  const updateStatus = async (targetIndex) => {
    // setLoading(true);
    setIsValueUpdated(false);
    try {
      if (targetIndex >= picklistValues.length || targetIndex < 0) {
        message.error('No more steps to complete');
        return;
      }

      const newStatus = picklistValues[targetIndex];
      const fieldMeta = metadata?.fields.find(f => f._id === recordPathField);
      const fieldApiName = fieldMeta?.name;

      if (!fieldApiName) {
        message.error("Field API name not found for record path field");
        return;
      }

      const body = {
        object_name: objectName,
        data: {
          _id: record._id || "",
          [fieldApiName]: newStatus,
        },
      };


      const apiService = new ApiService(
        `${BASE_URL}/insert_or_update_records`,
        { 'Content-Type': 'application/json' },
        'POST',
        body
      );
      await apiService.makeCall();

      completeStep(newStatus);
      await fetchRecords();

      message.success('Status updated successfully');
    } catch (error) {
      message.error('Error updating status');
    } finally {
      //   setLoading(false);
      setSelectedIndex(null);
    }
  };

  const completeStep = (newStatus) => {
    setIsValueUpdated(true);
  }

  const cancelSelectedValue = () => {
    setIsValueUpdated(false);
    setSelectedIndex(null);
  }

  const fetchLayoutNActionButtons = useCallback(async (recordData = null) => {
    const currentRecord = recordData || record;

    try {
      setLayoutLoading(true);
      let detailLayoutAndActionButtons = [];

      if (currentRecord?.RecordType) {
        const layoutService = new ApiService(
          `${BASE_URL}/get_layout_by_record_type/${objectName}/${currentRecord.RecordType}`,
          {},
          'GET'
        );
        const layoutResponse = await layoutService.makeCall();

        detailLayoutAndActionButtons = [layoutResponse];
      } else {
        const fallbackLayoutService = new ApiService(
          `${BASE_URL}/get_by_objectName/${objectName}`,
          {},
          'GET'
        );
        const fallbackResponse = await fallbackLayoutService.makeCall();

        detailLayoutAndActionButtons = (Array.isArray(fallbackResponse) ? fallbackResponse : [fallbackResponse])
          .filter(layout => layout.active);
      }
      setMetadata(prevMetadata => ({
        ...prevMetadata,
        layout: detailLayoutAndActionButtons,
        actionButtons: detailLayoutAndActionButtons[0]?.action_button_ids || []
      }));
    } catch (error) {
      console.error('Error fetching layout', error);
    } finally {
      setLayoutLoading(false);
    }
  }, [record, objectName]);

  const fetchMetadata = useCallback(async () => {
    try {
      const [objectResponse, fieldsResponse] = await Promise.all([
        new ApiService(`${BASE_URL}/get_mt_object_by_name/${objectName}`, {}, 'GET').makeCall(),
        new ApiService(`${BASE_URL}/mt_fields/object/${objectName}`, {}, 'GET').makeCall()
      ]);

      // Try to fetch compact layout separately and handle failure gracefully
      let layoutResponse = null;
      try {
        layoutResponse = await new ApiService(`${BASE_URL}/get_compact_layout/${objectName}`, {}, 'GET').makeCall();
      } catch (layoutError) {
        console.warn('Compact layout not found for object:', objectName, layoutError);
        // Continue without compact layout
      }

      // Extract record path field with safe fallback
      const recordPathFieldId = layoutResponse?.record_path_field || layoutResponse?.[0]?.record_path_field || null;

      setMetadata({
        allowApprovals: objectResponse[0]?.allow_approvals,
        allowActivity: objectResponse[0]?.track_activities,
        trackFieldHistory: objectResponse[0]?.track_field_history,
        allowFiles: objectResponse[0]?.allow_files,
        creationView: objectResponse[0]?.record_creation_view,
        fields: fieldsResponse,
        recordPathField: recordPathFieldId,
        picklistValues: recordPathFieldId ? 
          (fieldsResponse.find((field) => field._id === recordPathFieldId)?.picklist_values || []) : [],
        compactLayout: layoutResponse || layoutResponse?.[0] || null,
        actionButtons: [],
        layout: [],
        allowComments: objectResponse[0]?.allow_comments,
      });

      setRecordPathField(recordPathFieldId);

      if (recordPathFieldId) {
        const picklistField = fieldsResponse.find(field => field?._id === recordPathFieldId);
        setPicklistValues(picklistField?.picklist_values || []);
        setPicklistFieldNameForRecordPath(picklistField?.name || '');
      } else {
        // Reset picklist-related states when no record path field is found
        setPicklistValues([]);
        setPicklistFieldNameForRecordPath('');
      }
    } catch (error) {
      console.error('Error fetching metadata', error);
      // Even if there's an error, try to set minimal metadata to allow other components to work
      setMetadata({
        allowApprovals: false,
        allowActivity: false,
        trackFieldHistory: false,
        allowFiles: false,
        creationView: null,
        fields: [],
        recordPathField: null,
        picklistValues: [],
        compactLayout: null,
        actionButtons: [],
        layout: [],
        allowComments: false,
      });
    }
  }, [objectName]);

  const fetchRelatedLists = useCallback(async () => {
    try {
      const relatedListService = new ApiService(
        `${BASE_URL}/related_lists/for_object/${objectName}`,
        {},
        'GET'
      );
      const response = await relatedListService.makeCall();
      setRelatedLists(response || []);
    } catch (err) {
      setRelatedLists([]);
      console.error('Error fetching related records', err);
    }
  }, [objectName]);



  useEffect(() => {
    if (gridLayoutData) {
      const mappedLayout = gridLayoutData?.grid_layout?.map((item) => ({
        ...item?.position,
        i: item?.position?.i.toString(),
      }));

      const mappedComponents = gridLayoutData?.components?.map((item) => ({
        ...item?.info,
      }));

      const compact = mappedComponents?.find((comp) => comp?.componentType === "standard" && comp?.selectedComponentName === "CompactLayout");
 const otherComponents = mappedComponents?.filter((comp) => comp?.selectedComponentName !== "CompactLayout");
      setCompactComponent(compact);
      setComponents(otherComponents);
      setLayout(mappedLayout);
    }
  }, [gridLayoutData]);

  useEffect(() => {
    const initializeData = async () => {
      if (initializingRef.current) {
        return;
      }

      initializingRef.current = true;

      try {
        setLoading(true);
        await Promise.all([
          fetchMetadata(),
          fetchRelatedLists()
        ]);

        const recordData = await fetchRecords();

        // Finally fetch layout based on the record data
        if (recordData && Object.keys(recordData).length > 0) {
          await fetchLayoutNActionButtons(recordData);
        } else {
          await fetchLayoutNActionButtons({});
        }
      } catch (error) {
        console.error('Error initializing data:', error);
      } finally {
        // Only set loading to false after ALL operations are complete
        setLoading(false);
        initializingRef.current = false;
      }
    };

    initializeData();
  }, [objectName, recordId, fetchMetadata, fetchRecords, fetchRelatedLists, fetchLayoutNActionButtons]);

  if (loading || !metadata) {
    return <Spin />;
  }

  return (
    <>
      {transientLoading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.6)', // semi-transparent white overlay
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Spin size="large" />
          <div style={{
            marginTop: 12,
            fontSize: 16,
            fontWeight: 500,
            color: '#333'
          }}>
            {loadingMessage || "Hold on, your file is being downloaded..."}
          </div>
        </div>
      )}

      {/* <div style={styles.cardsContainer}> */}
      {compactComponent && (
        <div style={{ marginLeft: 10, marginRight: 8 }}>
          <RenderStandardComponent
            component={compactComponent}
            recordId={recordId}
            objectName={objectName}
            functionRef={functionRef}
            metadata={metadata}
            record={record}
            fetchRecords={fetchRecords}
            setRecord={setRecord}
            overallLoading={loading || layoutLoading}
            getRowStyle={getRowStyleForView}
            multipleHighlights={compactComponent?.multipleHighlights}
          />
        </div>
      )}
      <ResponsiveGridLayout
        className="my-5 mx-8"
        breakpoints={breakpoints}
        cols={cols}
        rowHeight={rowHeight}
        layout={layout}
        draggableCancel=".non-draggable"
        resizeHandles={[]}
        isDraggable={false}
        isResizable={false}
      >
        {components.map((component) => {
          // Check for relatedLists component and its existence in relatedLists
          if (
            component?.componentType === 'standard' &&
            component?.selectedComponentName === 'relatedLists'
          ) {
            const exists = relatedLists?.some(
              rl => rl?.related_list?._id === component?.selectedRelatedList
            );
            if (!exists) return null;
          }

          return (
            <div
              className="non-draggable"
              key={component?.id}
              data-grid={layout.find((item) => item.i === component?.id.toString())}
            >
              {/* <div className='non-draggable'> */}
              {component?.componentType === 'standard' ? (
                <>
                  <RenderStandardComponent
                    component={component}
                    recordId={recordId}
                    objectName={objectName}
                    functionRef={functionRef}
                    metadata={metadata}
                    overallLoading={loading || layoutLoading}
                    record={record}
                    fetchRecords={fetchRecords}
                    setRecord={setRecord}
                    tableProperties={component?.tableProperties}
                    updateStatus={updateStatus}
                    picklistValues={picklistValues}
                    selectedIndex={selectedIndex}
                    currentSelectedValue={currentSelectedValue}
                    completeStep={completeStep}
                    setSelectedIndex={setSelectedIndex}
                    isValueUpdated={isValueUpdated}
                    cancelSelectedValue={cancelSelectedValue}
                    picklistFieldNameForRecordPath={picklistFieldNameForRecordPath}
                    alerts={alerts}
                    // Pass highlight config for relatedLists using our local function
                    getRowStyle={getRowStyleForView}
                    multipleHighlights={component?.multipleHighlights}
                  />
                </>
              ) : component?.componentType === 'custom' ? (
                <RenderCustomComponent component={component} recordId={recordId} objectName={objectName} />
              ) : (
                <div style={styles.placeholder} className="non-draggable">
                  Please configure your component by clicking on the configure icon.
                </div>
              )}

            </div>
          );
        })}
      </ResponsiveGridLayout>
    </>
  )
}

const styles = {
  cardsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    width: "100%",
    justifyContent: "center",
    flexGrow: 1,
    height: "auto",
  },
  placeholder: {
    color: '#aaa',
    textAlign: 'center',
    marginTop: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  }
};

export default RecordGridLayoutView